import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { adminLogin, adminForgotSendOTP, adminForgotReset, adminStaffLogin } from '../../services/adminService';
import AppLogo from '../../components/common/AppLogo';

// ── Admin Forgot Password Modal ───────────────────────────────────────────
// Recovery phone is fixed (set once by admin in settings)
// Admin only enters username + OTP + new password
function AdminForgotModal({ onClose }) {
  const [step, setStep]       = useState(1); // 1=username, 2=OTP+new password
  const [adminName, setAdminName] = useState('');
  const [otp, setOtp]         = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [masked, setMasked]   = useState(''); // masked recovery phone shown to admin
  const [cooldown, setCooldown] = useState(0);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const sendOTP = async () => {
    if (!adminName.trim()) { toast.error('Enter your admin username'); return; }
    setLoading(true);
    try {
      const res = await adminForgotSendOTP({ adminName: adminName.trim() });
      setMasked(res.masked || '');
      toast.success('OTP sent to your recovery phone!');
      setStep(2);
      setCooldown(60);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally { setLoading(false); }
  };

  const resendOTP = async () => {
    if (cooldown > 0) return;
    setLoading(true);
    try {
      const res = await adminForgotSendOTP({ adminName: adminName.trim() });
      setMasked(res.masked || '');
      toast.success('OTP resent!');
      setCooldown(60);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally { setLoading(false); }
  };

  const resetPassword = async () => {
    if (otp.length !== 6)       { toast.error('Enter the 6-digit OTP'); return; }
    if (newPass.length < 6)     { toast.error('Password must be at least 6 characters'); return; }
    if (newPass !== confirmPass) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await adminForgotReset({ adminName: adminName.trim(), otp, newPassword: newPass });
      toast.success('Password reset! Please login with your new password.');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally { setLoading(false); }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4"
        onClick={onClose}>
        <div className="bg-white w-full max-w-sm rounded-2xl p-6 space-y-4 shadow-2xl"
          onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold text-text-primary">Reset Admin Password</h3>
              <div className="flex gap-1 mt-1">
                {[1,2].map(s => (
                  <div key={s} className={`h-1 rounded-full transition-all ${s <= step ? 'bg-primary w-8' : 'bg-border w-4'}`} />
                ))}
              </div>
            </div>
            <button onClick={onClose} className="btn-icon w-8 h-8">
              <i className="fa-solid fa-xmark text-text-secondary" />
            </button>
          </div>

          {step === 1 ? (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                <div className="flex items-start gap-2">
                  <i className="fa-solid fa-circle-info text-blue-500 text-sm mt-0.5" />
                  <p className="text-xs text-blue-700 leading-relaxed">
                    OTP will be sent to your <strong>fixed recovery phone number</strong>.
                    This number was set once during admin setup.
                  </p>
                </div>
              </div>

              <div>
                <label className="input-label">Admin Username</label>
                <input value={adminName}
                  onChange={e => setAdminName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendOTP()}
                  placeholder="Enter your admin username"
                  className="input" autoFocus />
              </div>

              <button onClick={sendOTP} disabled={loading || !adminName.trim()} className="btn-primary">
                {loading
                  ? <span className="flex items-center justify-center gap-2"><i className="fa-solid fa-spinner animate-spin" />Sending OTP...</span>
                  : <span><i className="fa-solid fa-paper-plane mr-2" />Send OTP</span>}
              </button>
            </>
          ) : (
            <>
              {/* Recovery phone hint */}
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-success text-sm" />
                    <div>
                      <p className="text-xs font-semibold text-green-800">OTP sent to {masked}</p>
                      <button onClick={resendOTP} disabled={cooldown > 0 || loading}
                        className="text-[10px] text-primary font-medium">
                        {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend OTP'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* OTP */}
              <div>
                <label className="input-label">6-digit OTP</label>
                <input type="tel" value={otp} maxLength={6}
                  onChange={e => setOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
                  placeholder="Enter OTP"
                  className="input text-center tracking-[0.5em] text-lg font-bold" />
              </div>

              {/* New password */}
              <div className="relative">
                <label className="input-label">New Password</label>
                <input type={showPass ? 'text' : 'password'} value={newPass}
                  onChange={e => setNewPass(e.target.value)}
                  placeholder="Min 6 characters" className="input pr-11" />
                <button onClick={() => setShowPass(p => !p)}
                  className="absolute right-3 bottom-3 text-text-muted text-sm">
                  <i className={`fa-solid ${showPass ? 'fa-eye-slash' : 'fa-eye'}`} />
                </button>
              </div>

              {/* Confirm password */}
              <div>
                <label className="input-label">Confirm Password</label>
                <input type={showPass ? 'text' : 'password'} value={confirmPass}
                  onChange={e => setConfirmPass(e.target.value)}
                  placeholder="Re-enter password" className="input" />
                {confirmPass && newPass !== confirmPass && (
                  <p className="text-xs text-danger mt-1 flex items-center gap-1">
                    <i className="fa-solid fa-circle-xmark" /> Passwords do not match
                  </p>
                )}
              </div>

              <button onClick={resetPassword}
                disabled={loading || otp.length !== 6 || !newPass || newPass !== confirmPass}
                className="btn-primary">
                {loading
                  ? <span className="flex items-center justify-center gap-2"><i className="fa-solid fa-spinner animate-spin" />Resetting...</span>
                  : <span><i className="fa-solid fa-key mr-2" />Reset Password</span>}
              </button>

              <button onClick={() => { setStep(1); setOtp(''); }}
                className="w-full text-sm text-text-muted text-center">
                ← Change username
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ── Admin Login Page ──────────────────────────────────────────────────────
export default function AdminLoginPage() {
  const { slug }          = useParams();
  const navigate          = useNavigate();
  const { loginSuccess }  = useAuth();
  const [loginType, setLoginType] = useState('admin'); // 'admin' | 'staff'
  const [form, setForm]   = useState({ adminName: '', username: '', password: '' });
  const [showPass, setShowPass]   = useState(false);
  const [loading, setLoading]     = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleLogin = async () => {
    if (!form.password) { toast.error('Password required'); return; }
    setLoading(true);
    try {
      if (loginType === 'admin') {
        if (!form.adminName) { toast.error('Username required'); setLoading(false); return; }
        const res = await adminLogin(slug, { adminName: form.adminName, password: form.password });
        loginSuccess(res.token, { ...(res.admin || {}), adminName: form.adminName }, 'admin');
        toast.success('Welcome back!');
        navigate('/admin/dashboard');
      } else {
        if (!form.username) { toast.error('Username required'); setLoading(false); return; }
        const res = await adminStaffLogin({ username: form.username, password: form.password, slug });
        loginSuccess(res.token, {
          ...(res.staff || {}),
          adminName: res.staff?.name,
          isAdminStaff: true,
          permissions: res.staff?.permissions,
        }, 'admin');
        toast.success(`Welcome, ${res.staff?.name}!`);
        // Navigate to first permitted section
        const perms = res.staff?.permissions || {};
        if (perms.doctors)      navigate('/admin/doctors');
        else if (perms.patients)     navigate('/admin/patients');
        else if (perms.appointments) navigate('/admin/appointments');
        else if (perms.payments)     navigate('/admin/payments');
        else navigate('/admin/dashboard');
      }
    } catch (err) {
      toast.error(err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="card-p space-y-5">
          <div className="text-center pb-2">
            <div className="flex justify-center mb-3">
              <AppLogo size="md" />
            </div>
            <h1 className="text-lg font-bold text-text-primary font-heading">Admin Login</h1>
          </div>

          {/* Login type tabs */}
          <div className="flex gap-1 bg-surface rounded-xl p-1 border border-border">
            {[{key:'admin',label:'Main Admin'},{key:'staff',label:'Staff Login'}].map(t => (
              <button key={t.key} onClick={() => setLoginType(t.key)}
                className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${
                  loginType === t.key ? 'bg-white shadow-sm text-primary' : 'text-text-muted'
                }`}>
                <i className={`fa-solid ${t.key === 'admin' ? 'fa-shield-halved' : 'fa-user-tie'} mr-1.5`} />
                {t.label}
              </button>
            ))}
          </div>

          <div>
            <label className="input-label">{loginType === 'admin' ? 'Admin Username' : 'Staff Username'}</label>
            {loginType === 'admin' ? (
              <input value={form.adminName}
                onChange={e => set('adminName', e.target.value)}
                placeholder="Admin username" className="input" autoFocus />
            ) : (
              <input value={form.username}
                onChange={e => set('username', e.target.value)}
                placeholder="Staff username" className="input" autoFocus />
            )}
          </div>

          <div>
            <label className="input-label">Password</label>
            <div className="relative">
              <input type={showPass ? 'text' : 'password'}
                value={form.password}
                onChange={e => set('password', e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
                placeholder="Password" className="input pr-11" />
              <button onClick={() => setShowPass(!showPass)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted">
                <i className={`fa-solid ${showPass ? 'fa-eye-slash' : 'fa-eye'}`} />
              </button>
            </div>
          </div>

          {/* Forgot password link — admin only */}
          {loginType === 'admin' && (
            <div className="text-right -mt-2">
              <button onClick={() => setShowForgot(true)}
                className="text-xs text-primary font-medium hover:underline">
                Forgot password?
              </button>
            </div>
          )}

          <button onClick={handleLogin} disabled={loading} className="btn-primary">
            {loading
              ? <span className="flex items-center justify-center gap-2">
                  <i className="fa-solid fa-spinner animate-spin" />Logging in...
                </span>
              : 'Login'}
          </button>
        </div>
      </div>

      {showForgot && <AdminForgotModal onClose={() => setShowForgot(false)} />}
    </div>
  );
}
